import debounce from 'lodash/debounce';
import {
  clearAttribute,
  clearAttributesObj,
  formatAttributesObjWithRef,
  fullfillAttributesObjWithRef,
  fullfillAttributeWithRef,
  isAttributeEqualToRef,
  isAttributesObjEqualToRef,
} from '@/store/baseModule/utils';
import { ActionTree } from 'vuex';
import BaseState from '@/store/baseModule/baseModule';
import { OrdsQueryParams } from '@/business/apiTreatment/request/reusableBaseRequest';
import { Service } from '@/services/services/tools/baseService';

/**
 * Debounce delay before a search is actually triggered
 */
const SEARCH_DEBOUNCE_DELAY = 1000;

interface MakeQueryParamOpts {
  entities: any[];
  attributesObj: any;
  search: string;
  pageSize: number;
  page: number;
  sort: string;
  sortBy: string;
  dateMin: Date;
  dateMax: Date;
  globalSearch: string;
}

export { SEARCH_DEBOUNCE_DELAY };
export type MakeHttpParamsUrlType = (params: any) => string;
export type MakeQueryParamsType = (
  params: MakeQueryParamOpts
) => OrdsQueryParams;

interface ActionsOpts<T> {
  makeHttpParamsUrl?: MakeHttpParamsUrlType;
  customActions?: any;
  makeQueryParams?: MakeQueryParamsType;
  serviceAPI?: Service<T>;
  defaultSortBy?: string[];
  defaultASC?: boolean[];
}

export default function<T>({
  makeHttpParamsUrl,
  customActions,
  makeQueryParams,
  serviceAPI,
  defaultSortBy,
  defaultASC,
}: ActionsOpts<T>): ActionTree<BaseState<T>, any> {
  return {
    initItems({ commit, dispatch, getters, rootGetters }) {
      if (getters.requestItems) {
        getters.requestItems.cancel(
          `API call cancelled because of duplicated call: ${serviceAPI.api.url}`
        );
      }
      let queryParams;
      if (makeQueryParams) {
        queryParams = makeQueryParams({
          entities: rootGetters['entities/current'],
          attributesObj: getters.attributesObj,
          search: getters.search,
          pageSize: getters.pageSize,
          page: getters.page,
          sort: getters.sort,
          sortBy: getters.sortBy,
          dateMin: getters.dateMin,
          dateMax: getters.dateMax,
          globalSearch: getters.globalSearch,
        });
      }
      let variableUrl;
      if (makeHttpParamsUrl) {
        if (getters.urlParamId) {
          variableUrl = makeHttpParamsUrl(getters.urlParamId);
        } else {
          // eslint-disable-next-line no-console
          console.error(
            `API call cancelled because of urlParamId was not set for variableUrl: ${makeHttpParamsUrl(
              getters.urlParamId
            )}`
          );
          return null;
        }
      }
      commit('SET_ITEMS_REQUEST', queryParams);
      return serviceAPI
        .getItems(
          {
            params: queryParams,
            cancelToken: getters.requestItems.token,
          },
          variableUrl
        )
        .then((items) => {
          commit('SET_ITEMS', items);
        })
        .catch((error) => {
          commit('SET_ITEMS_ERROR', error);
          dispatch(
            'snackbar/setError',
            { url: serviceAPI.api.url, error },
            { root: true }
          );
        });
    },
    initItemsWithDelay: debounce(({ dispatch }) => {
      dispatch('initItems');
    }, SEARCH_DEBOUNCE_DELAY),

    async initCurrent({ commit }, id) {
      commit('SET_CURRENT_REQUEST', id);
      return serviceAPI
        .getCurrent(id)
        .then((current) => {
          commit('SET_CURRENT', current);
        })
        .catch((error) => commit('SET_CURRENT_ERROR', JSON.stringify(error)));
    },
    reset({ commit }) {
      commit('SET_ITEMS', []);
    },
    setItemsPageSize({ commit, dispatch }, pageSizeIndex) {
      commit('SET_ITEMS_PAGE_SIZE', pageSizeIndex);
      commit('SET_ITEMS_PAGE', 1);
      dispatch('initItems');
    },
    setItemsSortBy({ commit, dispatch }, value) {
      commit('SET_ITEMS_SORT_BY', value || defaultSortBy);
      commit('SET_ITEMS_PAGE', 1);
      dispatch('initItems');
    },
    setItemsPage({ commit, dispatch }, page) {
      commit('SET_ITEMS_PAGE', page);
      dispatch('initItems');
    },
    refreshItems({ dispatch }) {
      dispatch('initItems');
    },
    setFilterAttribute(
      { commit, dispatch, getters },
      { attribute, serverSide, values, useDelay, reload = true }
    ) {
      let newServerSide = serverSide;
      if (serverSide === undefined) {
        newServerSide = true;
      }
      commit('SET_ATTRIBUTES_OBJ', {
        ...getters.attributesObj,
        [attribute]: {
          ...getters.attributesObj[attribute],
          serverSide: newServerSide,
          values,
        },
      });

      commit('SET_ITEMS_PAGE', 1);
      if (newServerSide && reload) {
        // eslint-disable-next-line no-unused-expressions
        useDelay ? dispatch('initItemsWithDelay') : dispatch('initItems');
      }
    },
    setCurrent({ commit }, current) {
      commit('SET_CURRENT', current);
    },
    toogleViewTable({ commit, getters }) {
      commit('SET_VIEW_TABLE', !getters.isViewTable);
    },
    setEditMode({ commit }, editMode) {
      commit('SET_EDIT_MODE', editMode);
    },
    setSelectedItems({ commit }, selectedItems) {
      commit('SET_SELECTED_ITEMS', selectedItems);
    },
    toogleOrderASC({ commit, getters, dispatch }) {
      commit('SET_ORDER_ASC', !getters.sort.ASC);
      commit('SET_ITEMS_PAGE', 1);
      dispatch('initItems');
    },
    setOrderASC({ commit, dispatch }, val) {
      commit('SET_ORDER_ASC', val || defaultASC);
      commit('SET_ITEMS_PAGE', 1);
      dispatch('initItems');
    },
    setAttributesObjRef(
      { commit, getters },
      { attribute, autocomplete, serverSide, values }
    ) {
      commit('SET_ATTRIBUTES_OBJ_REF', {
        ...getters.attributesObjRef,
        [attribute]: {
          serverSide,
          autocomplete,
          values,
        },
      });
    },
    setAttributesObj({ commit }, attributesObj) {
      commit('SET_ATTRIBUTES_OBJ', attributesObj);
    },
    setAttributesObjRefLoading({ commit, getters }, isLoading) {
      commit('SET_ATTRIBUTES_OBJ_REF_LOADING', isLoading);
      if (!isLoading) {
        commit(
          'SET_ATTRIBUTES_OBJ',
          formatAttributesObjWithRef({
            attributesObjRef: getters.attributesObjRef,
            attributesObj: getters.attributesObj,
          })
        );
      }
    },
    clearAttributesObj({ commit, dispatch, getters }) {
      commit(
        'SET_ATTRIBUTES_OBJ',
        clearAttributesObj({
          attributesObj: getters.attributesObj,
          attributesObjRef: getters.attributesObjRef,
        })
      );
      commit('SET_ITEMS_PAGE', 1);
      dispatch('initItemsWithDelay');
    },
    toggleAttributesObj({ commit, dispatch, getters }) {
      if (
        isAttributesObjEqualToRef({
          attributesObjRef: getters.attributesObjRef,
          attributesObj: getters.attributesObj,
        })
      ) {
        commit(
          'SET_ATTRIBUTES_OBJ',
          clearAttributesObj({
            attributesObj: getters.attributesObj,
            attributesObjRef: getters.attributesObjRef,
          })
        );
      } else {
        commit(
          'SET_ATTRIBUTES_OBJ',
          fullfillAttributesObjWithRef({
            attributesObj: getters.attributesObj,
            attributesObjRef: getters.attributesObjRef,
          })
        );
      }
      commit('SET_ITEMS_PAGE', 1);
      dispatch('initItemsWithDelay');
    },
    toggleAttribute({ commit, dispatch, getters }, attribute) {
      if (
        isAttributeEqualToRef({
          attributesObjRef: getters.attributesObjRef,
          attributesObj: getters.attributesObj,
          attribute,
        })
      ) {
        commit(
          'SET_ATTRIBUTES_OBJ',
          clearAttribute({
            attributesObj: getters.attributesObj,
            attributesObjRef: getters.attributesObjRef,
            attribute,
          })
        );
      } else {
        commit(
          'SET_ATTRIBUTES_OBJ',
          fullfillAttributeWithRef({
            attributesObj: getters.attributesObj,
            attributesObjRef: getters.attributesObjRef,
            attribute,
          })
        );
      }
      commit('SET_ITEMS_PAGE', 1);
      dispatch('initItemsWithDelay');
    },
    clearFilters({ commit, getters, dispatch }) {
      if (getters.hasDefaultFilters) {
        commit('SET_FILTERS_AS_DEFAULT');
      } else {
        commit(
          'SET_ATTRIBUTES_OBJ',
          clearAttributesObj({
            attributesObj: getters.attributesObj,
            attributesObjRef: getters.attributesObjRef,
          })
        );
        commit('SET_DATE_MIN', null);
        commit('SET_DATE_MAX', null);
        commit('SET_ITEMS_PAGE', 1);
      }
      dispatch('initItems');
    },
    setGlobalSearch({ commit, getters, dispatch }, value) {
      commit('SET_GLOBAL_SEARCH', value);
      getters.searchAttributesArray.map((attribute) =>
        commit('SET_SEARCH_$OR', { attribute, value })
      );
      commit('SET_ITEMS_PAGE', 1);
      dispatch('initItemsWithDelay');
    },
    setSearchOnAttribute({ commit, dispatch }, { payload }) {
      commit('SET_SEARCH_$AND', payload);
      commit('SET_ITEMS_PAGE', 1);
      dispatch('initItemsWithDelay');
    },
    setUrlParamId({ commit, dispatch }, urlParamId) {
      commit('SET_URL_PARAM_ID', urlParamId);
      commit('SET_ITEMS_PAGE', 1);
      dispatch('initItems');
    },
    setUrlParamIdAndClearFilters({ commit, dispatch }, urlParamId) {
      commit('SET_URL_PARAM_ID', urlParamId);
      dispatch('clearFilters');
    },
    setDatesFilter(
      { commit, dispatch, getters },
      { dateMax, dateMin, attribute }
    ) {
      if (!attribute) {
        commit('SET_DATE_MIN', dateMin);
        commit('SET_DATE_MAX', dateMax);
      } else {
        commit('SET_ATTRIBUTES_OBJ', {
          ...getters.attributesObj,
          [attribute]: {
            ...getters.attributesObj[attribute],
            values: [dateMin, dateMax],
          },
        });
      }
      commit('SET_ITEMS_PAGE', 1);
      dispatch('initItemsWithDelay');
    },
    removeItem({ commit }, id) {
      commit('REMOVE_ITEM', id);
    },
    updateItems({ commit }, items) {
      commit('UPDATE_ITEMS', items);
    },
    ...customActions,
  };
}
